import React from 'react';
import { motion } from 'framer-motion';
import { Globe, Cpu, Database, Zap, Shield, Layout, ArrowUpRight, BarChart3, Cloud, GraduationCap, Sprout, Landmark, Truck, RadioTower, Droplets, ArrowRight } from 'lucide-react';
import Hero from '@/components/marketing/Hero';
import PageTransition from '@/components/global/PageTransition';
import SEO from '@/components/global/SEO';
import { Content, Language } from '@/types';
import { Link } from 'react-router-dom';
import { ProductVisual } from '@/components/ui/ProductVisual';
import { CircularSatelliteRadar } from '@/components/ui/CircularSatelliteRadar';

interface HomeProps {
  content: Content;
  lang: Language;
  session?: any;
}

const Home: React.FC<HomeProps> = ({ content, lang, session }) => {
  const isRtl = lang === 'ar';

  //  Animation Lock: Skip entrance animations if session was already initialized
  const hasAnimated = typeof window !== 'undefined' && sessionStorage.getItem('appLoaded') === 'true';

  const productEcosystem = {
    en: [
      {
        name: "VEXL Nova",
        status: "Live",
        desc: "AI mentor and workspace for GIS students, academic researchers, and early spatial engineers.",
        icon: <GraduationCap className="text-[#0E93A3]" />,
        path: "/products/nova",
        visualType: "nova" as const,
        image: "/images/products/nova.webp"
      },
      {
        name: "VEXL GeoAgent",
        status: "Private Preview",
        desc: "Agentic spatial reasoning and GIS automation engine for multi-step geospatial workflows.",
        icon: <RadioTower className="text-[#0E93A3]" />,
        path: "/products/geo",
        visualType: "geo" as const,
        image: "/images/products/geo.webp"
      },
      {
        name: "VEXL CropLens",
        status: "Research",
        desc: "Satellite intelligence for crop verification, agricultural damage review, and fraud-risk signals.",
        icon: <Sprout className="text-[#0E93A3]" />,
        path: "/products/croplens",
        visualType: "croplens" as const,
        image: "/images/products/croplens.webp"
      },
      {
        name: "VEXL DroughtWatch",
        status: "UNEP Live",
        desc: "Predictive climate intelligence forecasting spatial drought risks and water stress 6 months in advance.",
        icon: <Droplets className="text-[#0E93A3]" />,
        path: "/products/droughtwatch",
        visualType: "drought" as const,
        image: "/images/products/drought.webp"
      }
    ],
    ar: [
      {
        name: "VEXL Nova",
        status: "متاح",
        desc: "موجه ومساحة عمل ذكاء اصطناعي لطلاب GIS والباحثين والمهندسين المكانيين في بداية الطريق.",
        icon: <GraduationCap className="text-[#0E93A3]" />,
        path: "/products/nova",
        visualType: "nova" as const,
        image: "/images/products/nova.webp"
      },
      {
        name: "VEXL GeoAgent",
        status: "معاينة خاصة",
        desc: "محرك تفكير مكاني وكيل لأتمتة سير عمل GIS متعدد الخطوات.",
        icon: <RadioTower className="text-[#0E93A3]" />,
        path: "/products/geo",
        visualType: "geo" as const,
        image: "/images/products/geo.webp"
      },
      {
        name: "VEXL CropLens",
        status: "بحث وتطوير",
        desc: "ذكاء أقمار صناعية للتحقق من المحاصيل ومراجعة الأضرار وإشارات مخاطر الاحتيال الزراعي.",
        icon: <Sprout className="text-[#0E93A3]" />,
        path: "/products/croplens",
        visualType: "croplens" as const,
        image: "/images/products/croplens.webp"
      },
      {
        name: "VEXL DroughtWatch",
        status: "معتمد أممياً",
        desc: "ذكاء مناخي تنبؤي لتحديد مخاطر الجفاف المائي وحماية الرقعة الزراعية قبل 6 أشهر.",
        icon: <Droplets className="text-[#0E93A3]" />,
        path: "/products/droughtwatch",
        visualType: "drought" as const,
        image: "/images/products/drought.webp"
      }
    ]
  }[lang];

  const industries = {
    en: [
      {
        id: "education-research",
        title: "Education & Research",
        desc: "GIS learning, thesis workflows, and academic spatial reasoning.",
        image: "/images/industries/education-research.webp",
        fallbackImage: "/images/industries/education-research.png"
      },
      {
        id: "agriculture-insurance",
        title: "Agriculture & Crop Insurance",
        desc: "Satellite verification for crop health, claims, and risk evidence.",
        image: "/images/industries/agriculture-insurance.webp",
        fallbackImage: "/images/industries/agriculture-insurance.png"
      },
      {
        id: "banking-risk",
        title: "Banking & Spatial Risk",
        desc: "Location-aware risk signals for assets, portfolios, and urban exposure.",
        image: "/images/industries/banking-risk.webp",
        fallbackImage: "/images/industries/banking-risk.png"
      },
      {
        id: "government-smart-cities",
        title: "Government & Smart Cities",
        desc: "Spatial decision systems for urban planning and public operations.",
        image: "/images/industries/government-smart-cities.webp",
        fallbackImage: "/images/industries/government-smart-cities.png"
      },
      {
        id: "environmental-monitoring",
        title: "Environmental Monitoring",
        desc: "Remote sensing workflows for land, water, vegetation, and climate signals.",
        image: "/images/industries/environmental-monitoring.webp",
        fallbackImage: "/images/industries/environmental-monitoring.png"
      },
      {
        id: "logistics-infrastructure",
        title: "Logistics & Infrastructure",
        desc: "Routing, coverage, network planning, and operational geospatial intelligence.",
        image: "/images/industries/logistics-infrastructure.webp",
        fallbackImage: "/images/industries/logistics-infrastructure.png"
      }
    ],
    ar: [
      {
        id: "education-research",
        title: "التعليم والبحث",
        desc: "تعلم GIS ومشاريع التخرج والتفكير المكاني الأكاديمي.",
        image: "/images/industries/education-research.webp",
        fallbackImage: "/images/industries/education-research.png"
      },
      {
        id: "agriculture-insurance",
        title: "الزراعة وتأمين المحاصيل",
        desc: "تحقق بالأقمار الصناعية لصحة المحاصيل والمطالبات وإثباتات المخاطر.",
        image: "/images/industries/agriculture-insurance.webp",
        fallbackImage: "/images/industries/agriculture-insurance.png"
      },
      {
        id: "banking-risk",
        title: "البنوك والمخاطر المكانية",
        desc: "إشارات مخاطر مرتبطة بالموقع للأصول والمحافظ والتعرض العمراني.",
        image: "/images/industries/banking-risk.webp",
        fallbackImage: "/images/industries/banking-risk.png"
      },
      {
        id: "government-smart-cities",
        title: "الحكومات والمدن الذكية",
        desc: "أنظمة قرار مكاني للتخطيط العمراني والعمليات العامة.",
        image: "/images/industries/government-smart-cities.webp",
        fallbackImage: "/images/industries/government-smart-cities.png"
      },
      {
        id: "environmental-monitoring",
        title: "المراقبة البيئية",
        desc: "سير عمل استشعار عن بعد للأرض والمياه والغطاء النباتي والمناخ.",
        image: "/images/industries/environmental-monitoring.webp",
        fallbackImage: "/images/industries/environmental-monitoring.png"
      },
      {
        id: "logistics-infrastructure",
        title: "اللوجستيات والبنية التحتية",
        desc: "توجيه وتغطية وتخطيط شبكات وذكاء مكاني تشغيلي.",
        image: "/images/industries/logistics-infrastructure.webp",
        fallbackImage: "/images/industries/logistics-infrastructure.png"
      }
    ]
  }[lang];

  return (
    <PageTransition>
      <div dir={isRtl ? 'rtl' : 'ltr'} className={isRtl ? 'font-arabic' : 'font-sans'} style={{ fontFeatureSettings: '"ss01" on' }}>
        <SEO
          title={isRtl ? 'فكسل VEXL — ذكاء اصطناعي متخصص في GIS والاستشعار عن بعد' : 'VEXL — Spatial AI for GIS & Remote Sensing'}
          description={isRtl
            ? "فكسل (VEXL) هي الشركة الأم لحلول الذكاء الاصطناعي الجغرافي والاستشعار عن بعد. نقوم بنشر منتجات متطورة تشمل VEXL Nova المخصص للطلاب و CropLens للتدقيق الزراعي طيفياً وكشف الاحتيال."
            : "VEXL is the parent Spatial AI company specializing in GIS & Remote Sensing, publishing advanced intelligence tools including VEXL Nova for students and CropLens for satellite-powered crop damage auditing."
          }
          lang={lang}
          breadcrumbs={[
            { name: "Home", item: "/" }
          ]}
          schema={[
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "@id": "https://vexl-gis.tech/#organization",
              "name": "VEXL",
              "url": "https://vexl-gis.tech",
              "logo": "https://vexl-gis.tech/logo.png",
              "description": isRtl
                ? "فكسل هي الشركة الأم لحلول الذكاء الاصطناعي الجغرافي والاستشعار عن بعد (GIS & RS)، وتقوم بتطوير منتجات رائدة مثل VEXL Nova لمساعدة الطلاب و CropLens للتحقق من الأراضي الزراعية طيفياً."
                : "VEXL is the parent Spatial AI organization specializing in Geographic Information Systems (GIS) and Remote Sensing (RS) technologies, publishing products like VEXL Nova and CropLens.",
              "founder": {
                "@type": "Person",
                "name": "Diaa Shousha",
                "jobTitle": "Founder & Chief AI Architect"
              },
              "hasPart": [
                {
                  "@type": "SoftwareApplication",
                  "name": "VEXL Nova",
                  "url": "https://vexl-gis.tech/products/nova",
                  "description": "Student-centric GIS and Remote Sensing AI assistant."
                },
                {
                  "@type": "SoftwareApplication",
                  "name": "VEXL CropLens",
                  "url": "https://vexl-gis.tech/products/croplens",
                  "description": "Satellite-powered crop damage verification and audit engine."
                }
              ],
              "sameAs": [
                "https://www.linkedin.com/company/vexl-studio",
                "https://x.com/vexl_tech",
                "https://github.com/vexl-gis",
                "https://www.instagram.com/vexl_official/"
              ]
            }
          ]}
        />

        {/* 1. Interactive Stripe-Inspired Gradient Mesh Hero */}
        <Hero lang={lang} />

        {/* 2. Showcase Product Mockup Section — Clean Layout Card */}
        <section className="pt-2 pb-8 md:pb-10 bg-white dark:bg-[#05070A] px-6 relative z-20 transition-colors duration-500">
          <div className="max-w-[1400px] mx-auto">
            
            {/* Desktop Layout (xl and above) */}
            <div className="hidden xl:grid grid-cols-12 gap-8 lg:gap-12 items-start">

              {/* Left Column (Sticky Large Product Image in deep navy mockup layout) */}
              <div className="col-span-9 sticky top-20 flex flex-col gap-5">
                <Link
                  to="/benchmarks"
                  className="relative w-full aspect-[1672/941] rounded-xl overflow-hidden border border-[#262C31] bg-[#12161A] dark:bg-[#0B0F12] transition-all duration-300 block group"
                  onContextMenu={(e) => e.preventDefault()}
                >
                  <img
                    src="/images/geoai-featured.webp"
                    alt="Featured GeoAI"
                    loading="lazy"
                    className="w-full h-full object-cover select-none pointer-events-none opacity-85"
                    draggable={false}
                    onContextMenu={(e) => e.preventDefault()}
                  />
                  {/* Subtle dark gradient overlay behind the text for maximum contrast and legibility */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent z-10 pointer-events-none" />
                  
                  {/* Overlay Title */}
                  <div className="absolute bottom-6 left-6 md:bottom-8 md:left-8 z-20 pointer-events-none">
                    <h3 className="text-3xl md:text-5xl font-medium text-white tracking-[-0.96px] font-sans" style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', sans-serif" }}>
                      VEXL Nova
                    </h3>
                  </div>
                </Link>
                <div className="min-h-[84px] flex flex-col justify-start">
                  <span className="text-[11px] text-[#66707A] font-mono font-medium tracking-[1.2px] mb-2 block uppercase" style={{ fontFeatureSettings: '"tnum"' }}>
                    May 08, 2026
                  </span>
                  <Link to="/benchmarks" className="text-xl md:text-[22px] font-medium text-[#EDF0F1] tracking-tight hover:text-[#0E93A3] transition-colors cursor-pointer w-fit block">
                    {lang === 'ar' ? 'نقدم لكم VEXL Nova 2.1' : 'Introducing VEXL Nova 2.1'}
                  </Link>
                </div>
              </div>

              {/* Right Column (Scrolling Sub-features) */}
              <div className="col-span-3 flex flex-col gap-16 mt-12 lg:mt-0 lg:py-0">
                {[
                  {
                    img: "/images/spatial-ml.webp",
                    title: lang === 'ar' ? "تجربة متكاملة للذكاء المكاني عبر الهواتف" : "Seamless Spatial Intelligence on Mobile",
                    date: "May 12, 2026",
                    imageClasses: "object-cover object-top",
                    link: "/spatial-mobile"
                  },
                  {
                    img: "/images/gis-workflow.webp",
                    title: lang === 'ar' ? "إطلاق برنامج VEXL Nova لأجهزة سطح المكتب" : "Introducing the VEXL Nova Desktop Application",
                    date: "May 08, 2026",
                    imageClasses: "object-cover object-center",
                    link: "/nova-desktop"
                  },
                  {
                    img: "/images/palantir-integration.webp",
                    title: lang === 'ar' ? "سير عمل مؤسسي للبيانات المكانية والأنطولوجيا" : "Enterprise Spatial Data Ontology Workflows",
                    date: "May 01, 2026",
                    imageClasses: "object-cover object-center",
                    link: "/docs"
                  }
                ].map((article, idx) => (
                  <Link to={article.link} key={idx} className="group cursor-pointer flex flex-col gap-5 block">
                    <div className="relative aspect-square rounded-xl overflow-hidden border border-[#262C31] bg-[#0B0F12]" onContextMenu={(e) => e.preventDefault()}>
                      <img
                        src={article.img}
                        alt={article.title}
                        loading="lazy"
                        className={`w-full h-full ${article.imageClasses} select-none pointer-events-none group-hover:scale-102 transition-transform duration-500`}
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                      />
                    </div>
                    <div className="min-h-[84px] flex flex-col justify-start">
                      {/* Tabular numeric alignment for dates */}
                      <span className="text-[11px] text-[#66707A] font-mono font-medium tracking-[1.2px] mb-2 block uppercase" style={{ fontFeatureSettings: '"tnum"' }}>
                        {article.date}
                      </span>
                      <h4 className="text-lg md:text-[22px] font-medium text-[#EDF0F1] group-hover:text-[#0E93A3] transition-colors leading-snug tracking-tight">
                        {article.title}
                      </h4>
                    </div>
                  </Link>
                ))}
              </div>

            </div>

            {/* Mobile/Tablet/Small Laptop Layout (below xl) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 xl:hidden">
              {[
                {
                  img: "/images/geoai-featured.webp",
                  title: lang === 'ar' ? 'نقدم لكم VEXL Nova 2.1' : 'Introducing VEXL Nova 2.1',
                  date: "May 08, 2026",
                  imageClasses: "object-cover",
                  link: "/benchmarks"
                },
                {
                  img: "/images/spatial-ml.webp",
                  title: lang === 'ar' ? "تجربة متكاملة للذكاء المكاني عبر الهواتف" : "Seamless Spatial Intelligence on Mobile",
                  date: "May 12, 2026",
                  imageClasses: "object-cover object-top",
                  link: "/spatial-mobile"
                },
                {
                  img: "/images/gis-workflow.webp",
                  title: lang === 'ar' ? "إطلاق برنامج VEXL Nova لأجهزة سطح المكتب" : "Introducing the VEXL Nova Desktop Application",
                  date: "May 08, 2026",
                  imageClasses: "object-cover object-center",
                  link: "/nova-desktop"
                },
                {
                  img: "/images/palantir-integration.webp",
                  title: lang === 'ar' ? "سير عمل مؤسسي للبيانات المكانية والأنطولوجيا" : "Enterprise Spatial Data Ontology Workflows",
                  date: "May 01, 2026",
                  imageClasses: "object-cover object-center",
                  link: "/docs"
                }
              ].map((article, idx) => (
                <Link to={article.link} key={idx} className="group cursor-pointer flex flex-col gap-5 block">
                  <div className="relative aspect-square rounded-xl overflow-hidden border border-[#262C31] bg-[#12161A] dark:bg-[#0B0F12]" onContextMenu={(e) => e.preventDefault()}>
                    <img
                      src={article.img}
                      alt={article.title}
                      loading="lazy"
                      className={`w-full h-full ${article.imageClasses} select-none pointer-events-none group-hover:scale-102 transition-transform duration-500`}
                      draggable={false}
                      onContextMenu={(e) => e.preventDefault()}
                    />
                  </div>
                  <div className="min-h-[84px] flex flex-col justify-start">
                    <span className="text-[11px] text-[#66707A] font-mono font-medium tracking-[1.2px] mb-2 block uppercase" style={{ fontFeatureSettings: '"tnum"' }}>
                      {article.date}
                    </span>
                    <h4 className="text-lg md:text-xl font-medium text-[#EDF0F1] group-hover:text-[#0E93A3] transition-colors leading-snug tracking-tight">
                      {article.title}
                    </h4>
                  </div>
                </Link>
              ))}
            </div>

          </div>
        </section>

        {/* 3. VEXL Product Ecosystem — Adaptive Canvas Surface */}
        <section className="pt-8 pb-8 md:pt-10 md:pb-10 bg-[#05070A] px-6 relative z-20 transition-colors duration-500">
          <div className="max-w-[1400px] mx-auto">
            <motion.div
              initial={hasAnimated ? false : { opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mb-16"
            >
              <span className="text-[#0E93A3] text-[11px] font-bold font-mono uppercase tracking-[0.22em]">
                {lang === 'ar' ? 'منظومة منتجات VEXL' : 'VEXL Product Ecosystem'}
              </span>
              <h2 className="mt-4 text-3xl md:text-[42px] font-medium text-[#EDF0F1] tracking-[-0.84px] leading-[1.15]" style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', sans-serif" }}>
                {lang === 'ar'
                  ? 'شركة ذكاء اصطناعي واحدة، ومنتجات مكانية متعددة.'
                  : 'One AI company, multiple spatial intelligence products.'}
              </h2>
              <p className="mt-5 text-[#9AA5AC] text-base md:text-[17px] leading-[1.6] font-normal">
                {lang === 'ar'
                  ? 'VEXL ليست منتجاً واحداً. نحن نبني عائلة أنظمة ذكاء اصطناعي متخصصة في GIS والاستشعار عن بعد، تبدأ من التعليم وتمتد إلى الزراعة والبنوك والمدن والعمليات واسعة النطاق.'
                  : 'VEXL is not a single-product startup. We are building a family of AI systems specialized in GIS and remote sensing, starting with education and expanding into agriculture, banking, cities, and large-scale operations.'}
              </p>
            </motion.div>

            <div className="flex lg:grid overflow-x-auto lg:overflow-x-visible snap-x snap-mandatory lg:snap-none lg:grid-cols-4 gap-5 pb-8 lg:pb-0 scrollbar-none -mx-6 px-6 lg:mx-0 lg:px-0 scroll-pl-6 scroll-pr-6">
              {productEcosystem.map((product, idx) => (
                <motion.div
                  key={product.name}
                  initial={hasAnimated ? false : { opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, delay: idx * 0.08 }}
                  className="w-[72vw] sm:w-[38vw] lg:w-auto shrink-0 snap-start snap-always"
                >
                  <Link
                    to={product.path}
                    className="group h-full flex flex-col justify-between rounded-xl bg-[#12161A] border border-[#262C31] p-5 hover:border-[#0E93A3] transition-all duration-300"
                  >
                    <div>
                      {/* Product Visual Preview Mockup */}
                      <div className="mb-6">
                        <ProductVisual type={product.visualType} image={product.image} />
                      </div>

                      <div className="flex items-center justify-between gap-4 mb-4">
                        <h3 className="text-xl md:text-[22px] font-medium tracking-tight text-[#EDF0F1]">
                          {product.name}
                        </h3>
                        {/* Subdued Teal/Green Tag Pill */}
                        <span className="px-2.5 py-1 rounded-full bg-[#0B2818] border border-[#27500A]/40 text-[#C0DD97] text-[9px] font-bold font-mono uppercase tracking-[0.18em] whitespace-nowrap">
                          {product.status}
                        </span>
                      </div>

                      <p className="text-sm leading-[1.55] text-[#9AA5AC] font-normal">
                        {product.desc}
                      </p>
                    </div>
                    <div className="pt-6 flex items-center justify-between text-xs font-bold font-mono uppercase tracking-[0.18em] text-[#9AA5AC] group-hover:text-[#0E93A3] transition-colors">
                      <span>{lang === 'ar' ? 'استكشف' : 'Explore'}</span>
                      <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 4. Industries VEXL is building for — Adaptive Canvas Soft backdrop */}
        <section className="pt-8 pb-8 md:pt-10 md:pb-10 bg-[#05070A] px-6 relative z-20 transition-colors duration-500">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
              <div className="max-w-3xl">
                <span className="text-[#0E93A3] text-[11px] font-bold font-mono uppercase tracking-[0.22em]">
                  {lang === 'ar' ? 'القطاعات التي نبني لها' : 'Industries We Are Building For'}
                </span>
                <h2 className="mt-4 text-3xl md:text-[42px] font-medium text-[#EDF0F1] tracking-[-0.84px] leading-[1.15]" style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', sans-serif" }}>
                  {lang === 'ar'
                    ? 'من الجامعة إلى البنوك والحكومات.'
                    : 'From the university lab to banks and governments.'}
                </h2>
              </div>
              <p className="max-w-md text-[#9AA5AC] text-sm md:text-[15px] leading-[1.55] font-normal">
                {lang === 'ar'
                  ? 'هذه ليست وعود مبيعات، بل خريطة المنتج: أين يمكن للذكاء المكاني أن يصبح طبقة قرار حقيقية.'
                  : 'This is not a sales promise; it is the product map for where spatial AI becomes a real decision layer.'}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#262C31] border border-[#262C31] rounded-2xl overflow-hidden shadow-sm">
              {industries.map((industry) => (
                <div
                  key={industry.title}
                  className="bg-[#12161A] p-7 md:p-8 min-h-[240px] flex flex-col justify-between"
                >
                  <div>
                    {/* 240x240 High-Res Rounded Edge Visual Container (Balanced 80px) */}
                    <div className="mb-6 w-20 h-20 rounded-2xl overflow-hidden border border-white/10 bg-black shadow-md flex items-center justify-center shrink-0">
                      <img
                        src={industry.image}
                        alt={industry.title}
                        onError={(e) => {
                          const target = e.currentTarget;
                          if (target.src.endsWith('.webp')) {
                            target.src = industry.fallbackImage;
                          }
                        }}
                        className="w-full h-full object-cover select-none pointer-events-none"
                        loading="lazy"
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                      />
                    </div>

                    <h3 className="text-lg md:text-[21px] font-medium tracking-tight text-[#EDF0F1] mb-3">
                      {industry.title}
                    </h3>
                    <p className="text-sm leading-[1.6] text-[#9AA5AC] font-normal">
                      {industry.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Ecosystem Compatibility Bar */}
        <section className="py-12 bg-[#05070A] px-6 relative z-20 border-t border-[#262C31] transition-colors duration-500">
          <div className="max-w-[1400px] mx-auto">
            <p className="text-center text-[#66707A] dark:text-[#66707A] text-[11px] font-bold font-mono uppercase tracking-[0.18em] mb-6">
              {lang === 'ar'
                ? 'مصمم للعمل عبر منظومات GIS والاستشعار عن بعد'
                : 'Designed to work across GIS and remote sensing ecosystems'}
            </p>
            <div className="flex flex-wrap justify-center items-center gap-3">
              {[
                {
                  icon: (
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="8" />
                      <path d="M12 16 L16 20" />
                      <path d="M10 8 L14 12" />
                    </svg>
                  ),
                  label: 'QGIS workflows'
                },
                {
                  icon: (
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2 L2 7 L12 12 L22 7 Z" />
                      <path d="M2 17 L12 22 L22 17" />
                      <path d="M2 12 L12 17 L22 12" />
                    </svg>
                  ),
                  label: 'Esri ArcPy'
                },
                {
                  icon: (
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 3 A9 9 0 0 0 15 21" />
                      <path d="M12 3 A9 9 0 0 1 9 21" />
                      <path d="M3 12 H21" />
                    </svg>
                  ),
                  label: 'Google Earth Engine'
                },
                { icon: <BarChart3 size={14} />, label: 'Sentinel & Landsat' },
                { icon: <Cpu size={14} />, label: 'Enterprise PostGIS' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2 rounded-full border border-[#262C31] bg-[#12161A] px-4 py-2 text-[#9AA5AC] text-xs font-medium">
                  {item.icon}
                  <span style={{ fontFeatureSettings: '"ss01"' }}>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 6. Final Strategic CTA Card */}
        <section className="bg-[#05070A] pt-8 pb-12 md:pt-10 md:pb-16 relative z-20 transition-colors duration-500">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div
              className="bg-[#12161A] rounded-xl p-10 md:p-16 border border-[#262C31]"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.8 }}
            >
              <div className="text-center max-w-2xl mx-auto">
                <h2 className="text-3xl md:text-[42px] font-medium text-[#EDF0F1] tracking-[-0.84px] mb-8 leading-[1.15]" style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', sans-serif" }}>
                  {lang === 'ar' ? 'ابدأ عصر الاستخبارات المكانية الجديد' : 'Begin the next era of Spatial Intelligence'}
                </h2>
                <div className="flex justify-center mb-12">
                  <Link
                    to="/products/nova"
                    className="px-6 py-3 rounded-sm bg-[#0E93A3] hover:bg-[#075C68] text-white font-medium text-sm tracking-wide transition-all duration-300 inline-flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-[#0E93A3] focus:ring-offset-2"
                  >
                    <span>{lang === 'ar' ? 'ابدأ الآن' : 'Get Started'}</span>
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 pt-12 border-t border-[#262C31]">
                <div>
                  <h3 className="text-lg font-medium text-[#EDF0F1] mb-4">
                    {lang === 'ar' ? 'ما هو VEXL؟' : 'What is VEXL?'}
                  </h3>
                  <p className="text-[#9AA5AC] text-sm leading-[1.55] font-normal">
                    {lang === 'ar'
                      ? 'VEXL هي شركة ذكاء اصطناعي متخصصة في GIS والاستشعار عن بعد. نبني منتجات مكانية تبدأ بتعليم الطلاب وتمتد إلى الزراعة والبنوك والمدن والعمليات واسعة النطاق.'
                      : 'VEXL is an AI company specialized in GIS and remote sensing. We build spatial products that start with student learning and expand into agriculture, banking, cities, and large-scale operations.'
                    }
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-[#EDF0F1] mb-4">
                    {lang === 'ar' ? 'لماذا تختار VEXL؟' : 'Why Choose VEXL?'}
                  </h3>
                  <p className="text-[#9AA5AC] text-sm leading-[1.55] font-normal">
                    {lang === 'ar'
                      ? 'لأننا لا نبني روبوت دردشة عام. نبني أنظمة تفهم المكان والخرائط والأقمار الصناعية وسير عمل GIS الحقيقي.'
                      : 'Because we are not building a generic chatbot. We build systems that understand place, maps, satellites, and real GIS workflows.'
                    }
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </PageTransition>
  );
};

export default Home;
